import React from "react";
import { FaArrowRight, FaMapMarkerAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import logo from "../assets/images/logo-transparent.png";
import usePageMeta from "../hooks/usePageMeta";
import useScrollReveal from "../hooks/useScrollReveal";

const experiences = [
  {
    period: "Depuis janvier 2026",
    title: "Intégrateur Web Sénior · Migration Bootstrap 3 → 5",
    company: "Guarani",
    location: "33650 Martillac",
    context: "Mission chez Conecteo / Kiamo",
    bullets: [
      {
        lead: "Contexte produit",
        text:
          "intervention sur les interfaces d’administration et l’interface web agent d’une solution omnicanale dédiée à la gestion des interactions clients.",
      },
      {
        lead: "Migration Bootstrap 3 → 5",
        text:
          "mise à niveau progressive de la base front existante vers une version plus moderne, stable et maintenable.",
      },
      {
        lead: "Migration Less → SCSS",
        text:
          "refonte des styles vers Dart Sass avec un découpage modulaire plus propre et plus évolutif.",
      },
      {
        lead: "Refactor des interfaces",
        text:
          "adaptation des templates Twig, fichiers PHP et vues BackboneJS pour garantir la compatibilité avec la nouvelle stack front.",
      },
      {
        lead: "Modernisation technique",
        text:
          "remplacement progressif des composants obsolètes, réduction de la dépendance à jQuery et mise à jour des patterns UI.",
      },
      {
        lead: "Qualité & non-régression",
        text:
          "contrôle du responsive, de l’accessibilité et de la stabilité fonctionnelle et visuelle des interfaces.",
      },
      {
        lead: "Travail transverse",
        text:
          "coordination avec les équipes produit, développement et QA, avec documentation des conventions front et des bonnes pratiques de migration.",
      },
    ],
  },
  {
    period: "2025",
    title: "Développeur Full-Stack Sénior",
    company: "Extia",
    location: "33600 Pessac",
    bullets: [
      {
        lead: "Contexte client",
        text: "intervention sur des projets du secteur aéronautique pour Airbus.",
      },
      {
        lead: "Développement full-stack",
        text:
          "conception et évolution d’applications web avec React.js côté frontend et Node.js côté backend.",
      },
      {
        lead: "Base de données",
        text: "modélisation et gestion de données sous PostgreSQL.",
      },
      {
        lead: "Cloud & déploiement",
        text: "intégration continue et déploiement sur AWS (EC2, S3, Lambda).",
      },
      {
        lead: "Qualité logicielle",
        text:
          "rédaction et maintenance de tests unitaires avec Jest pour sécuriser les évolutions.",
      },
      {
        lead: "Collaboration GitHub",
        text: "gestion du code source, pull requests et revues de code.",
      },
      {
        lead: "Méthodologie agile",
        text: "participation aux sprints, revues, planifications et démonstrations.",
      },
      {
        lead: "Travail transverse",
        text: "coordination avec les équipes DevOps, QA et architecture.",
      },
      {
        lead: "Performance",
        text:
          "optimisation des performances frontend et backend selon des contraintes techniques élevées.",
      },
    ],
  },
  {
    period: "2024",
    title: "Développeur Web · Site web, identité de marque & stratégie marketing",
    company: "Relax Time Massages",
    location: "33330 Saint-Sulpice-de-Faleyrens",
    link: "https://relaxtimemassages.fr/",
    linkLabel: "www.relaxtimemassages.fr",
    bullets: [
      {
        lead: "Création du site web",
        text:
          "conception et développement d’un site vitrine sur mesure avec React, Node.js et Tailwind CSS.",
      },
      {
        lead: "Formulaire sécurisé",
        text: "mise en place d’un formulaire de contact fiable et protégé.",
      },
      {
        lead: "Prise de rendez-vous",
        text: "intégration d’un agenda en ligne pour faciliter la réservation.",
      },
      {
        lead: "Identité de marque",
        text:
          "définition d’une charte graphique cohérente avec l’univers bien-être de l’activité.",
      },
      {
        lead: "Supports de communication",
        text: "création du logo, de flyers, de pancartes et de cartes de visite.",
      },
      {
        lead: "Stratégie marketing",
        text:
          "accompagnement sur le positionnement, la visibilité locale et les premiers leviers d’acquisition client.",
      },
      {
        lead: "SEO local",
        text: "optimisation du site pour améliorer la présence locale sur les moteurs de recherche.",
      },
      {
        lead: "Mise en ligne",
        text: "déploiement sur un VPS sécurisé avec Docker et Nginx.",
      },
      {
        lead: "Autonomie client",
        text:
          "formation à la prise en main des outils digitaux et sensibilisation aux bonnes pratiques de sécurité.",
      },
    ],
  },
  {
    period: "2020 – 2024",
    title: "CDI · Développeur Web",
    company: "Patrimoine Store",
    location: "33240 Saint-André-de-Cubzac",
    bullets: [
      {
        lead: "Sites & outils digitaux",
        text: "développement de solutions web adaptées aux besoins des clients.",
      },
      {
        lead: "CMS & contenus",
        text: "utilisation de HubSpot pour la gestion et la publication de contenus.",
      },
      {
        lead: "Développement sur mesure",
        text:
          "backend sous Symfony et intégration frontend avec Tailwind CSS pour des outils orientés commerciaux.",
      },
      {
        lead: "Acquisition",
        text: "création de landing pages optimisées pour la génération de leads.",
      },
      {
        lead: "Simulations métiers",
        text: "développement de simulateurs personnalisés pour l’investissement immobilier.",
      },
      {
        lead: "SEO éditorial",
        text:
          "intégration et publication d’articles de blog pour soutenir la stratégie de référencement.",
      },
      {
        lead: "Analyse des besoins",
        text: "recueil des attentes clients et proposition de solutions adaptées.",
      },
      {
        lead: "UX/UI",
        text: "optimisation des parcours et des interfaces pour améliorer l’expérience utilisateur.",
      },
      {
        lead: "Collaboration transverse",
        text: "travail avec les équipes marketing et commerciales sur des projets communs.",
      },
      {
        lead: "Infrastructure",
        text: "administration et maintenance de serveurs Linux.",
      },
      {
        lead: "SEO / SEA",
        text: "gestion et optimisation d’actions de référencement naturel et payant.",
      },
    ],
  },
  {
    period: "2018 – 2020",
    title: "Contrat de professionnalisation · Développeur Web",
    company: "Patrimoine Store",
    location: "33240 Saint-André-de-Cubzac",
    bullets: [
      {
        lead: "Développement web",
        text: "participation à la réalisation de sites et d’outils digitaux.",
      },
      {
        lead: "Travail d’équipe",
        text: "collaboration quotidienne avec un développeur backend et une graphiste.",
      },
      {
        lead: "HubSpot",
        text: "gestion de contenus et intégrations dans l’écosystème CMS.",
      },
      {
        lead: "Landing pages",
        text: "création de pages optimisées pour l’acquisition de prospects.",
      },
      {
        lead: "Symfony",
        text: "développement backend et frontend dans un environnement PHP.",
      },
      {
        lead: "Administration système",
        text: "gestion de serveurs Linux pour garantir la stabilité des environnements.",
      },
    ],
  },
  {
    period: "2018 · 3 mois",
    title: "Stage en développement web",
    company: "Patrimoine Store",
    location: "33240 Saint-André-de-Cubzac",
    bullets: [
      {
        lead: "Outils internes",
        text:
          "participation au développement d’outils internes et de pages dynamiques sous Symfony (PHP, Twig, JavaScript).",
      },
      {
        lead: "Intégration responsive",
        text:
          "intégration frontend en respectant la charte graphique avec HTML5 et CSS3.",
      },
      {
        lead: "Travail transverse",
        text:
          "collaboration étroite avec le développeur backend et la graphiste pour assurer la cohérence visuelle et technique.",
      },
      {
        lead: "Bonnes pratiques",
        text: "participation aux revues de code et à l’amélioration de la qualité des livrables.",
      },
      {
        lead: "UX/UI",
        text: "proposition d’optimisations ergonomiques pour améliorer l’expérience utilisateur.",
      },
      {
        lead: "Cycle projet",
        text:
          "découverte du cycle complet de développement en environnement agile : planification, développement, tests et livraison.",
      },
    ],
  },
  {
    period: "2016 · 6 mois",
    title: "Stage en développement web",
    company: "Cover House",
    location: "33310 Lormont",
    bullets: [
      {
        lead: "E-commerce",
        text: "développement et personnalisation de modules sur PrestaShop et Odoo.",
      },
      {
        lead: "Frontend & backend",
        text:
          "intégration de fonctionnalités avec HTML, CSS, JavaScript, PHP et Python.",
      },
      {
        lead: "Maintenance",
        text: "maintenance évolutive et corrective de plateformes e-commerce.",
      },
      {
        lead: "Site vitrine WordPress",
        text:
          "création d’un site dédié à un nouveau service de réparation de téléphones pour renforcer la visibilité en ligne.",
      },
      {
        lead: "Analyse fonctionnelle",
        text:
          "échanges avec les équipes métier pour cadrer les besoins et orienter les choix techniques.",
      },
      {
        lead: "Ergonomie",
        text:
          "participation à la réflexion UX/UI pour améliorer la présentation des offres.",
      },
      {
        lead: "Outils & données",
        text:
          "utilisation de MySQL, PostgreSQL et Git pour le développement et le suivi des versions.",
      },
    ],
  },
];

const skillGroups = [
  {
    title: "Langages",
    items: ["HTML5", "CSS3", "HubL", "JavaScript", "PHP", "SQL", "NoSQL", "Python"],
  },
  {
    title: "Frameworks & bibliothèques",
    items: [
      "Symfony",
      "Bootstrap 3/5",
      "Tailwind CSS",
      "UIkit",
      "Vue.js",
      "React.js",
      "Node.js",
      "BackboneJS",
    ],
  },
  {
    title: "Styles & templating",
    items: ["Sass/SCSS", "Less", "Twig", "Responsive design", "Flexbox", "CSS Grid"],
  },
  {
    title: "CMS & plateformes",
    items: ["HubSpot", "Shopify", "PrestaShop", "WordPress", "Odoo"],
  },
  {
    title: "Versioning & collaboration",
    items: ["Git", "GitLab", "GitHub", "CI/CD", "Code review", "Trello"],
  },
  {
    title: "Systèmes & déploiement",
    items: ["Docker", "Windows Server", "Linux", "Nginx", "Apache"],
  },
  {
    title: "Design & outils",
    items: [
      "Création de logos",
      "Supports visuels web & print",
      "GIMP",
      "Canva",
      "Visual Studio Code",
      "Google Analytics",
      "Google Calendar API",
      "Microsoft Office",
    ],
  },
  {
    title: "SEO & acquisition",
    items: [
      "Audit SEO",
      "Optimisation technique",
      "Recherche de mots-clés",
      "Analyse concurrentielle",
      "Google Ads",
      "Facebook Ads",
      "Rédaction optimisée",
    ],
  },
  {
    title: "Marketing digital",
    items: [
      "Newsletters",
      "Automatisation des campagnes",
      "Suivi des KPI",
      "Gestion de projet",
      "Suivi des livrables",
    ],
  },
];

function CVPage() {
  usePageMeta({
    title: "CV",
    description:
      "CV en ligne de Sofiane Amroune, développeur web sénior polyvalent, capable d’intervenir sur le frontend, le backend et la modernisation d’applications web.",
  });

  const { ref: heroRef, revealProps: heroRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.15,
  });

  const { ref: summaryRef, revealProps: summaryRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.15,
  });

  const { ref: experiencesRef, revealProps: experiencesRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.08,
  });

  const { ref: skillsRef, revealProps: skillsRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.1,
  });

  const { ref: endRef, revealProps: endRevealProps } = useScrollReveal({
    triggerOnce: true,
    threshold: 0.15,
  });

  return (
    <div className="page-shell space-y-8 lg:space-y-10">
      <section
        ref={heroRef}
        {...heroRevealProps}
        className="hero-panel scroll-reveal"
      >
        <div className="grid items-center gap-8 lg:grid-cols-[1.2fr,0.8fr]">
          <div>
            <span className="eyebrow">CV en ligne</span>
            <h1 className="hero-title">Mon CV</h1>
            <p className="hero-lead">
              <strong>Développeur web sénior orienté frontend, avec une expérience full-stack</strong>,
              j’interviens sur la conception, l’évolution et la fiabilisation d’applications web.
            </p>
            <p className="hero-copy">
              Je travaille principalement avec <strong>React, TypeScript, JavaScript, PHP / Symfony et Node.js</strong>,
              avec une attention particulière portée à la <strong>qualité des interfaces, la maintenabilité du code
              et l’expérience utilisateur</strong>.
            </p>
            <p className="hero-copy">
              Habitué aux projets existants comme aux développements sur mesure, je peux intervenir de
              l’intégration jusqu’au déploiement, dans des environnements <strong>Docker, Git et CI/CD</strong>.
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              <span className="meta-pill">React · TypeScript · JavaScript</span>
              <span className="meta-pill">PHP · Symfony · Node.js</span>
              <span className="meta-pill">Docker · Git · CI/CD</span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="button-primary">
                Me contacter
                <FaArrowRight className="ml-2" aria-hidden="true" />
              </Link>
              <Link to="/portfolio" className="button-secondary">
                Voir mes projets
              </Link>
            </div>
          </div>

          <div className="hidden justify-center lg:flex lg:justify-end">
            <div className="relative flex h-[18rem] w-[18rem] items-center justify-center rounded-full border border-brand/20 bg-white/80 shadow-panel sm:h-[20rem] sm:w-[20rem]">
              <div className="absolute inset-5 rounded-full border border-brand/15 bg-surfaceSoft/35" />
              <img
                src={logo}
                alt="Logo Sofiane Amroune"
                className="relative z-10 h-44 w-44 object-contain sm:h-52 sm:w-52"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="stat-card">
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-brandDeep">
              Positionnement
            </p>
            <p className="mb-2 text-base font-semibold leading-6 text-ink">
              Frontend React / TypeScript avec polyvalence full-stack
            </p>
            <p className="m-0 text-sm leading-6">
              Développement d’interfaces modernes, refonte et évolution d’applications existantes, avec
              la capacité d’intervenir également sur le backend lorsque le projet le nécessite.
            </p>
          </div>
          <div className="stat-card">
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-brandDeep">
              Environnement
            </p>
            <p className="mb-2 text-base font-semibold leading-6 text-ink">
              React, TypeScript, Symfony, Node.js
            </p>
            <p className="m-0 text-sm leading-6">
              Tailwind CSS, Bootstrap, Twig, PostgreSQL, MySQL, Docker, GitLab CI/CD et environnements Linux.
            </p>
          </div>
          <div className="stat-card">
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.18em] text-brandDeep">
              Approche
            </p>
            <p className="mb-2 text-base font-semibold leading-6 text-ink">
              Pragmatisme, qualité et maintenabilité
            </p>
            <p className="m-0 text-sm leading-6">
              Comprendre le besoin, proposer une solution claire, développer proprement et garder une
              attention constante à l’utilisateur final et à l’évolution du projet.
            </p>
          </div>
        </div>
      </section>

      <section
        ref={summaryRef}
        {...summaryRevealProps}
        className="scroll-reveal grid gap-6 md:grid-cols-2"
      >
        <article className="section-card">
          <span className="eyebrow">Informations</span>
          <h2 className="mb-4 text-3xl">Informations</h2>
          <ul className="space-y-3">
            <li><strong>Métier :</strong> Développeur web sénior</li>
            <li><strong>Orientation :</strong> Frontend React / TypeScript · Full-stack</li>
            <li><strong>Spécialités :</strong> Interfaces web · Refonte UI · Applications métier · Maintenance et évolution</li>
            <li><strong>Mobilité :</strong> Permis B · Véhicule</li>
          </ul>
        </article>

        <article className="section-card-soft">
          <span className="eyebrow">Profil</span>
          <h2 className="mb-4 text-3xl">Profil professionnel</h2>
          <p>
            <strong>Développeur web sénior</strong> avec plusieurs années d’expérience dans le développement
            et la maintenance d’applications web.
          </p>
          <p className="mt-4">
            Mon parcours m’a amené à intervenir sur des <strong>interfaces frontend, applications métier,
            migrations techniques et projets full-stack</strong>, dans des contextes et secteurs variés.
          </p>
          <p className="mt-4">
            Aujourd’hui, je souhaite mettre particulièrement à profit mon expérience sur des projets
            <strong> frontend React / TypeScript</strong>, tout en conservant la polyvalence technique acquise
            côté backend et déploiement.
          </p>
        </article>
      </section>

      <section
        ref={experiencesRef}
        {...experiencesRevealProps}
        className="section-card scroll-reveal"
      >
        <span className="eyebrow">Parcours</span>
        <h2 className="mb-6">Expériences professionnelles</h2>

        <div className="grid gap-6 lg:grid-cols-2">
          {experiences.map((experience) => (
            <article key={`${experience.period}-${experience.company}`} className="timeline-card">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <span className="meta-pill text-brandDeep">{experience.period}</span>
                {experience.location ? (
                  <span className="inline-flex items-center gap-2 text-sm text-muted">
                    <FaMapMarkerAlt className="text-brand" aria-hidden="true" />
                    {experience.location}
                  </span>
                ) : null}
              </div>

              <h3 className="mb-1">{experience.title}</h3>
              <p
                className={`text-sm font-semibold uppercase tracking-[0.18em] text-brandStrong ${
                  experience.context ? "mb-1" : "mb-4"
                }`}
              >
                {experience.company}
              </p>

              {experience.context ? <p className="mb-4 text-sm text-muted">{experience.context}</p> : null}

              {experience.link ? (
                <p className="mb-4 text-sm">
                  <a
                    className="content-link"
                    href={experience.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {experience.linkLabel}
                  </a>
                </p>
              ) : null}

              <ul className="list-disc space-y-2 pl-5 text-sm leading-6 marker:text-brand">
                {experience.bullets.map((item) => (
                  <li key={`${experience.period}-${item.lead}`} className="pl-1">
                    <strong className="font-semibold text-brandDeep">{item.lead} :</strong>{" "}
                    {item.text}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section
        ref={skillsRef}
        {...skillsRevealProps}
        className="section-card scroll-reveal"
      >
        <span className="eyebrow">Compétences</span>
        <h2 className="mb-6">Stack, outils et expertises</h2>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {skillGroups.map((group) => (
            <article
              key={group.title}
              className="rounded-soft border border-line bg-white/95 p-5 shadow-soft"
            >
              <h3 className="mb-4">{group.title}</h3>
              <div className="flex flex-wrap gap-2">
                {group.items.map((item) => (
                  <span key={item} className="skill-pill">
                    {item}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section
        ref={endRef}
        {...endRevealProps}
        className="scroll-reveal grid gap-6 md:grid-cols-2"
      >
        <article className="section-card">
          <span className="eyebrow">Formation</span>
          <h2 className="mb-4 text-3xl">Formation</h2>
          <p className="m-0">
            <strong>2015 – 2020 :</strong> Cycle ingénierie à l’EPSI Bordeaux · Expert Informatique
          </p>
        </article>

        <article className="section-card-soft">
          <span className="eyebrow">Centres d’intérêt</span>
          <h2 className="mb-4 text-3xl">Centres d’intérêt</h2>
          <ul className="space-y-3">
            <li>Sport : tennis, football, Formule 1</li>
            <li>Informatique et veille technologique</li>
            <li>Cuisine et pâtisserie</li>
          </ul>
        </article>
      </section>
    </div>
  );
}

export default CVPage;
